import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
// import { useQueryClient } from "@tanstack/react-query";

// Interfaces
import { ICrmContextProviderProps, ICrmData } from "./interfaces";

const CRMContext = createContext<ICrmData>({
	selection: null,
	locationId: "",
	tabs: [
		{ key: "leads", label: "Leads" },
		{ key: "dispositions", label: "Dispositions" },
		{ key: "customers", label: "Customers" },
		{ key: "social-media-reviews", label: "Social Media Reviews" },
	],
	tabSelected: 0,
	setTabSelected: () => {},
	isCustomerTabActive: false,
});

const useCRMStore = (store: ICrmData) => {
	return useMemo(() => store, [store]);
};

const isCustomerTab = (index: number, tabs: { key: string; label: string }[] = []) => {
	const tabKey = tabs[index]?.key ?? "";
	return tabKey === "customers";
};

const CRMContextProvider = (props: Readonly<ICrmContextProviderProps>) => {
	const {
		children = null,
		selection = null,
		cacheManagement = {
			clearAbsoluteIndexes: [],
		},
		selectionId = "",
		locationId = "",
		tabs = [
			{ key: "leads", label: "Leads" },
			{ key: "dispositions", label: "Dispositions" },
			{ key: "customers", label: "Customers" },
			{ key: "social-media-reviews", label: "Social Media Reviews" },
		],
	} = props ?? {};

	// States
	const [tabSelected, setTabSelected] = useState<number>(0);
	const [isCustomerTabActive, setIsCustomerTabActive] = useState<boolean>(false);

	// Actions
	const handleTabSelected = useCallback((index: number) => {
		const isCustomerSelected = isCustomerTab(index, tabs);
		setIsCustomerTabActive(isCustomerSelected);
	}, []);

	useEffect(() => {
		handleTabSelected(tabSelected);
	}, [tabSelected]);

	// Store build
	const store = useCRMStore({
		cacheManagement,
		isCustomerTabActive,
		selection,
		selectionId,
		locationId,
		tabs,
		tabSelected,
		setTabSelected,
	});

	useEffect(() => {
		return () => {};
	}, [locationId, selectionId]);

	return <CRMContext.Provider value={store}>{children}</CRMContext.Provider>;
};

function useCRMContext() {
	const context = useContext(CRMContext);
	if (!context) {
		throw new Error("useCRMContext must be used within a CRMContextProvider");
	}
	return context;
}

export { CRMContextProvider, useCRMContext };
